import { useQuery } from "@tanstack/react-query";
import Api from "src/Config";
import {
  createContext,
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Tooltip } from "@chakra-ui/react";

import { textTruncate } from "src/helpers/truncateText";

import { ReactComponent as MonitoredIcon } from "src/assets/images/monitored_circle.svg";

export const GetUserMonitoringBulkRecordContext = createContext(null);

type Props = {
  children: ReactElement;
};

const score_color: any = {
  LOW: "#05CD99",
  MEDIUM: "#F8961E",
  HIGH: "#F94144",
};

const score_bgcolor: any = {
  LOW: "#EBF9F1",
  MEDIUM: "#FEF2E5",
  HIGH: "#FBE7E8",
};

const GetUserMonitoringBulkRecordProvider: FunctionComponent<Props> = ({
  children,
}) => {
  const { id } = useParams();
  const [queryParams, setQueryParams] = useSearchParams();
  const pageQuery = queryParams.get("page") || 1;
  const bulkPageQuery = queryParams.get("bulk_page") || 1;

  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["bulk_check_history"],
    queryFn: async () => {
      const { data } = await Api.get(`onboarding/bulk/records/${id}`);

      return data;
    },
  });

  const {
    data: profileData,
    isLoading: isLoadingProfile,
    error: profileError,
    isFetching: isFetchingProfile,
  } = useQuery({
    queryKey: [
      "user_monitoring_bulk_record_list_history",
      searchValue,
      // pageQuery,
      bulkPageQuery,
      queryParams.toString(),
    ],
    // staleTime: 30000,
    queryFn: async () => {
      const url = searchValue
        ? `onboarding/bulk/records/${id}/profiles?search=${searchValue}&page_size=10&page=1`
        : `onboarding/bulk/records/${id}/profiles?page_size=10&page=${bulkPageQuery}
        `;
      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          first_name: string;
          last_name: string;
          other_name: string;
          source: string;
          created_at: string;
          email: string;
          risk_status: string;
          risk_score: number;
          business_name: string;
          is_monitor: boolean;
        }) => ({
          id: res.id,
          "": (
            <Tooltip
              label="This profile is being monitored"
              hasArrow
              placement="right-end"
              isDisabled={!res.is_monitor}
            >
              {res.is_monitor ? (
                <MonitoredIcon
                  style={{ width: "15px", height: "15px", cursor: "pointer" }}
                />
              ) : (
                <></>
              )}
            </Tooltip>
          ),
          name: `${
            !res.first_name?.toLowerCase() &&
            !res.last_name?.toLowerCase() &&
            !res.other_name?.toLowerCase() &&
            !res.business_name?.toLowerCase()
              ? "N/A"
              : res.business_name &&
                  !res.first_name?.toLowerCase() &&
                  !res.last_name?.toLowerCase() &&
                  !res.other_name?.toLowerCase()
                ? textTruncate(res.business_name, 20)
                : textTruncate(
                    `${res.first_name?.toLowerCase() || ""} ${
                      res.other_name?.toLowerCase() || ""
                    } ${res.last_name?.toLowerCase() || ""}`,
                    15,
                  )
          }`,
          source: `${res.source?.toLowerCase()} Check`,
          monitored: `${res.is_monitor ? "Yes" : "No"}`,
          date: format(new Date(res.created_at), "dd/MM/yyyy"),
          "email address": textTruncate(res.email?.toLowerCase(), 15) || "N/A",
          row_disabled: res.is_monitor,
          risk: res.risk_status,
          status: (
            <div
              style={{
                color: res.is_monitor ? "unset" : score_color[res.risk_status],
                background: res.is_monitor
                  ? "#00000008"
                  : score_bgcolor[res.risk_status],
                width: "fit-content",
                textTransform: "lowercase",
                padding: "2px 15px",
                borderRadius: "23.29px",
              }}
            >
              {res.risk_status}
            </div>
          ),
          "risk score": (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div
                style={{
                  background: "#D9DDE3",
                  width: "100%",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    background: score_color[res.risk_status],
                    height: "1.2vh",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    width: res.risk_score,
                  }}
                />
              </div>
              {res.risk_score.toFixed(2)}%
            </div>
          ),
        }),
      );
      return { response, metadata: data?.metadata };
    },
  });

  const handleSearch = (e: any) => {
    if (e.target.value) {
      setSearchParam(e.target.value);
    } else {
      setSearchValue("");
      setSearchParam("");
    }
  };

  const handleSearchRecord = () => {
    setSearchValue(searchParam);
  };

  useEffect(() => {
    if (profileData) {
      setPageCount(Math.ceil(profileData?.metadata.count / 10));
    }
  }, [profileData]);

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      isFetching,
      profileData,
      isLoadingProfile,
      isFetchingProfile,
      profileError,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      bulkPageQuery,
      queryParams,
      setQueryParams,
      handleSearch,
      searchParam,
      handleSearchRecord,
    }),
    [
      data,
      isLoading,
      isFetching,
      profileData,
      isLoadingProfile,
      isFetchingProfile,
      profileError,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      bulkPageQuery,
      queryParams,
      setQueryParams,
      handleSearch,
      searchParam,
      handleSearchRecord,
    ],
  );
  return (
    <GetUserMonitoringBulkRecordContext.Provider value={contextValue}>
      {children}
    </GetUserMonitoringBulkRecordContext.Provider>
  );
};

export default GetUserMonitoringBulkRecordProvider;
